/**
 * Fuzzy-match spoken or typed text to roster slugs.
 *
 * Shared by two mediums on purpose (SPEC Section 14): the Phase 2 audio box
 * speech-to-texts "alligator versus lion" and needs two slugs, and the Phase 1
 * web app's microphone button (Jeff's request, D-046) needs exactly the same
 * thing. Medium-agnostic, no dependencies, runs on the server or in the browser.
 *
 *   parseMatchup('alligator v lion')  -> { a: 'alligator', b: 'lion', ... }
 *   matchSlug('tyrannosaurus')        -> { slug: 'tyrannosaurus-rex', score: 0.86 }
 */

/** Things a kid or a speech engine says that are not the animal's slug. */
const ALIASES = {
  'trex': 'tyrannosaurus-rex', 't rex': 'tyrannosaurus-rex', 'tyrannosaurus': 'tyrannosaurus-rex',
  'tyrannosaurus rex': 'tyrannosaurus-rex', 'raptor': 'velociraptor', 'rex': 'tyrannosaurus-rex',
  'orca': 'killer-whale', 'killer whale': 'killer-whale', 'great white': 'great-white-shark',
  'white shark': 'great-white-shark', 'croc': 'saltwater-crocodile', 'crocodile': 'saltwater-crocodile',
  'gator': 'alligator', 'grizzly': 'grizzly-bear', 'polar bear': 'polar-bear', 'brown bear': 'grizzly-bear',
  'wolf': 'gray-wolf', 'grey wolf': 'gray-wolf', 'puma': 'cougar', 'mountain lion': 'cougar',
  'panther': 'leopard', 'african elephant': 'african-elephant', 'mammoth': 'woolly-mammoth',
  'sabertooth': 'sabertooth-cat', 'saber tooth tiger': 'sabertooth-cat', 'sabre tooth': 'sabertooth-cat',
  'saber tooth': 'sabertooth-cat', 'sabre tooth tiger': 'sabertooth-cat', 'saber toothed tiger': 'sabertooth-cat',
  'megalodon shark': 'megalodon', 'meg': 'megalodon', 'anaconda': 'green-anaconda',
  'python': 'reticulated-python', 'cobra': 'king-cobra', 'mamba': 'black-mamba',
  'komodo': 'komodo-dragon', 'eagle': 'bald-eagle',
  'falcon': 'peregrine-falcon', 'owl': 'great-horned-owl', 'octopus': 'giant-pacific-octopus',
  'squid': 'giant-squid', 'jellyfish': 'box-jellyfish', 'bees': 'killer-bee-swarm',
  'bee': 'killer-bee-swarm', 'killer bees': 'killer-bee-swarm', 'hornet': 'giant-hornet',
  'spider': 'tarantula', 'centipede': 'giant-centipede', 'kangaroo': 'red-kangaroo',
  'ape': 'gorilla', 'monkey': 'baboon', 'chimp': 'chimpanzee', 'boar': 'wild-boar',
  'hyena': 'spotted-hyena', 'badger': 'honey-badger', 'devil': 'tasmanian-devil',
  'thylacine': 'tasmanian-tiger', 'fox': 'red-fox',
  'seal': 'leopard-seal', 'turtle': 'leatherback-sea-turtle', 'eel': 'moray-eel',
  'ray': 'stingray', 'manta ray': 'stingray', 'stego': 'stegosaurus', 'trike': 'triceratops',
  'ankylo': 'ankylosaurus', 'spino': 'spinosaurus', 'brachio': 'brachiosaurus',
  'giganoto': 'giganotosaurus', 'carno': 'carnotaurus', 'pachy': 'pachycephalosaurus',
  'para': 'parasaurolophus', 'dart frog': 'poison-dart-frog', 'blue ringed octopus': 'blue-ringed-octopus',
  'bird eater': 'goliath-birdeater', 'widow': 'black-widow', 'black widow spider': 'black-widow',
  // Names kids actually use for animals already on the roster (D-076).
  // The horse lives on the farm now (D-209); "wild horse" was its old card.
  'pony': 'horse', 'horsey': 'horse', 'horsie': 'horse', 'foal': 'horse', 'colt': 'horse', 'filly': 'horse',
  'mare': 'horse', 'stallion': 'horse', 'mustang': 'horse', 'racehorse': 'horse', 'race horse': 'horse', 'wild horse': 'horse',
  'pterodactyl': 'pteranodon', 'ptero': 'pteranodon',
  'brontosaurus': 'apatosaurus', 'bronto': 'apatosaurus', 'long neck': 'apatosaurus',
  'puppy': 'domestic-dog', 'doggy': 'domestic-dog', 'doggie': 'domestic-dog', 'dog': 'domestic-dog',
  'kitty': 'house-cat', 'kitten': 'house-cat', 'cat': 'house-cat', 'pussycat': 'house-cat',
  'bunny': 'rabbit', 'bunny rabbit': 'rabbit',
  'piggy': 'pig', 'piglet': 'pig', 'hog': 'pig',
  'hen': 'chicken', 'rooster': 'chicken', 'chick': 'chicken', 'cockerel': 'chicken',
  'moo cow': 'cow', 'bull': 'cow', 'calf': 'cow', 'cattle': 'cow',
  'lamb': 'sheep', 'ram': 'sheep', 'ewe': 'sheep',
  'billy goat': 'goat', 'kid goat': 'goat',
  'duckling': 'duck', 'mallard': 'duck',
  'panda bear': 'giant-panda', 'panda': 'giant-panda',
  'penguin': 'emperor-penguin', 'macaw': 'parrot',
  'raven': 'crow', 'blackbird': 'crow',
  'porpoise': 'dolphin', 'flipper': 'dolphin',
  'puffer fish': 'pufferfish', 'blowfish': 'pufferfish', 'fugu': 'pufferfish',
  'snapper': 'snapping-turtle', 'boa': 'boa-constrictor',
  'monarch': 'butterfly', 'ladybird': 'ladybug',
  'sea lizard': 'mosasaurus', 'armoured fish': 'dunkleosteus',
  'wild cat': 'lynx', 'snow cat': 'snow-leopard',
  'possum': 'opossum', 'racoon': 'raccoon',
  // Ocean and reptile pass (D-078)
  'crab': 'coconut-crab', 'sea star': 'starfish', 'star fish': 'starfish',
  'urchin': 'sea-urchin', 'nemo': 'clownfish', 'anemone fish': 'clownfish',
  'sea horse': 'seahorse', 'humpback': 'humpback-whale', 'beluga whale': 'beluga',
  'sea cow': 'manatee', 'dugong': 'manatee',
  'angler fish': 'anglerfish', 'lantern fish': 'anglerfish',
  'sea serpent': 'oarfish', 'ribbon fish': 'oarfish',
  'taipan': 'inland-taipan', 'fierce snake': 'inland-taipan',
  'viper': 'gaboon-viper', 'gaboon': 'gaboon-viper',
  'jesus lizard': 'basilisk-lizard', 'basilisk': 'basilisk-lizard',
  'frill neck': 'frilled-lizard', 'frill necked lizard': 'frilled-lizard',
  'giant tortoise': 'galapagos-tortoise', 'tortoise': 'galapagos-tortoise',
  'lizard': 'iguana', 'monitor lizard': 'komodo-dragon',
  // A person on the roster (D-124): first name, surname, the misspelling, the nickname.
  'brock': 'brock-lesnar', 'lesnar': 'brock-lesnar', 'lesner': 'brock-lesnar', 'brock lesner': 'brock-lesnar',
  'beast': 'brock-lesnar', 'beast incarnate': 'brock-lesnar',
  // The rest of the Legends tab (D-153). "andre the giant" normalizes to "andre giant",
  // because normalize() drops the accent and "the" is a stopword — so the full name is
  // an alias too, not just the short ones.
  'andre': 'andre-the-giant', 'andre giant': 'andre-the-giant', 'andr giant': 'andre-the-giant',
  'eighth wonder': 'andre-the-giant',
  'thor': 'hafthor-bjornsson', 'hafthor': 'hafthor-bjornsson', 'bjornsson': 'hafthor-bjornsson',
  'mountain': 'hafthor-bjornsson', 'worlds strongest man': 'hafthor-bjornsson',
  'phelps': 'michael-phelps', 'michael': 'michael-phelps',
  'bolt': 'usain-bolt', 'usain': 'usain-bolt', 'lightning bolt': 'usain-bolt',
  'undertaker': 'the-undertaker', 'taker': 'the-undertaker', 'phenom': 'the-undertaker',
  'rock': 'the-rock', 'dwayne johnson': 'the-rock', 'dwayne': 'the-rock',
  'cena': 'john-cena', 'john': 'john-cena',
  'hogan': 'hulk-hogan', 'hulk': 'hulk-hogan', 'hulkamania': 'hulk-hogan',
  'rey': 'rey-mysterio', 'mysterio': 'rey-mysterio', '619': 'rey-mysterio',
  'ali': 'muhammad-ali', 'muhammad': 'muhammad-ali', 'cassius clay': 'muhammad-ali', 'greatest': 'muhammad-ali',
  // The two screen characters (D-181), behind the characters flag. A kid asks
  // for one of these by the show as often as by the name, and "cowboy" and
  // "biker" resolved to nothing before, so neither can shadow an animal.
  'rip': 'rip-wheeler', 'wheeler': 'rip-wheeler', 'yellowstone': 'rip-wheeler',
  'cowboy': 'rip-wheeler', 'cole hauser': 'rip-wheeler',
  'jax': 'jax-teller', 'jacks': 'jax-teller', 'teller': 'jax-teller', 'jackson teller': 'jax-teller',
  'sons of anarchy': 'jax-teller', 'samcro': 'jax-teller', 'biker': 'jax-teller', 'charlie hunnam': 'jax-teller',
  'sasquatch': 'bigfoot', 'big foot': 'bigfoot', 'squatch': 'bigfoot',
  'nessie': 'loch-ness-monster', 'loch ness': 'loch-ness-monster', 'ness monster': 'loch-ness-monster',
  'abominable snowman': 'yeti', 'snowman': 'yeti',
  'gryphon': 'griffin', 'griffon': 'griffin',
  'winged horse': 'pegasus', 'flying horse': 'pegasus',
  'fire dragon': 'dragon', 'fire breathing dragon': 'dragon',
  // The deer (D-164). The record is a white-tailed deer, so the whitetail names
  // point at it; "buck", "doe" and "fawn" are what a kid calls one in a field,
  // and "bambi" is what the 4-year-old calls one. "reindeer" needs no entry —
  // the fuzzy pass already lands it here, which is the closest card there is.
  'whitetail': 'deer', 'white tail': 'deer', 'white tailed deer': 'deer', 'whitetail deer': 'deer',
  'buck': 'deer', 'doe': 'deer', 'fawn': 'deer', 'stag': 'deer', 'bambi': 'deer',
  // The bobcat and the fisher (D-165). "wild cat" already points at the lynx and
  // stays there. "fisher cat" is the New England name for an animal that is
  // neither, and it is the one most people type — including the person who asked
  // for it — so it gets the entry the card's own name cannot give it.
  'bob cat': 'bobcat', 'bobtail cat': 'bobcat', 'red lynx': 'bobcat',
  'fisher cat': 'fisher', 'fishercat': 'fisher', 'fisher weasel': 'fisher', 'pekan': 'fisher',
  // A bare "fish" used to reach the swordfish on the substring rule; "Fisher" is
  // an earlier substring match and stole it. A kid who says "fish" means a fish.
  'fish': 'swordfish', 'fishes': 'swordfish',
  // The kiwi (D-166). A kid says "kiwi bird" as often as "kiwi", because the
  // other kiwi is a fruit; the roster has no fruit, so both land here.
  'kiwi bird': 'kiwi', 'kiwibird': 'kiwi', 'brown kiwi': 'kiwi',
  // The Superheroes tab (D-168), which is behind the heroes flag — so the two
  // names that were already taken list both slugs, most-wanted first, and
  // aliasSlug() picks the one this build actually has. With heroes off, "hulk"
  // is Hulk Hogan and "thor" is Hafthor Bjornsson exactly as before; with them
  // on, a kid saying either word means the one in the cape.
  'thor': ['thor', 'hafthor-bjornsson'],
  'hulk': ['the-hulk', 'hulk-hogan'], 'incredible hulk': 'the-hulk', 'bruce banner': 'the-hulk',
  'spiderman': 'spider-man', 'spidey': 'spider-man', 'web slinger': 'spider-man', 'peter parker': 'spider-man',
  'ironman': 'iron-man', 'tony stark': 'iron-man', 'stark': 'iron-man',
  'wonderwoman': 'wonder-woman', 'diana': 'wonder-woman', 'amazon princess': 'wonder-woman',
  'flash': 'the-flash', 'fastest man alive': 'the-flash', 'barry allen': 'the-flash',
  'man of steel': 'superman', 'clark kent': 'superman', 'kal el': 'superman',
  'dark knight': 'batman', 'bruce wayne': 'batman', 'caped crusader': 'batman',
  'god of thunder': 'thor', 'ororo': 'storm', 'weather witch': 'storm',
  'king of sea': 'aquaman', 'arthur curry': 'aquaman',
  // Two more heroes (D-180). "panther" is deliberately NOT one of them: it is
  // already the leopard's alias, a black panther IS a leopard, and a kid who
  // wants the king says both his words. Every key below reaches nothing on the
  // roster today, so none of them can shadow an animal.
  'tchalla': 'black-panther', 't challa': 'black-panther',
  'wakanda': 'black-panther', 'king of wakanda': 'black-panther',
  'steve rogers': 'captain-america', 'first avenger': 'captain-america',
  'super soldier': 'captain-america', 'supersoldier': 'captain-america',
}

/**
 * The slug an alias means on THIS build's roster (D-168). An alias may name
 * more than one slug — "hulk" is the green one when the heroes flag is on and
 * Hulk Hogan when it is off — and the first one actually on the roster wins.
 * It also stops any alias from ever returning a slug the caller does not have.
 */
function aliasSlug(q, animals) {
  const v = ALIASES[q]
  if (!v) return null
  const list = Array.isArray(v) ? v : [v]
  const have = new Set(animals.map((a) => a.slug))
  return list.find((slug) => have.has(slug)) || null
}

/** "a versus b", "a vs b", "a v b", "a against b", "a or b", "a and b". */
const SPLIT_RE = /\s+(?:versus|vs\.?|v\.?|against|fights?|beats?|or|and)\s+/i

export function normalize(text) {
  return String(text)
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, ' ')
    .replace(/\b(a|an|the|please|show|me|who|would|win|between)\b/g, ' ')
    .replace(/[-\s]+/g, ' ')
    .trim()
}

export const singular = (s) => s.replace(/\b(\w+?)(?:es|s)\b/g, (m, stem) => (m.length > 4 ? stem : m))

/** Levenshtein distance, capped for speed. */
function editDistance(x, y) {
  if (x === y) return 0
  const m = x.length
  const n = y.length
  if (!m || !n) return m || n
  let prev = Array.from({ length: n + 1 }, (_, j) => j)
  for (let i = 1; i <= m; i++) {
    const cur = [i]
    for (let j = 1; j <= n; j++) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (x[i - 1] === y[j - 1] ? 0 : 1))
    }
    prev = cur
  }
  return prev[n]
}

const similarity = (x, y) => 1 - editDistance(x, y) / Math.max(x.length, y.length)

/**
 * Best roster match for one spoken phrase.
 * @param {string} phrase
 * @param {{slug: string, name: string}[]} animals
 * @returns {{slug: string, score: number, via: string}|null} score 0..1; null below the floor
 */
export function matchSlug(phrase, animals, { floor = 0.6 } = {}) {
  const q = normalize(phrase)
  if (!q) return null

  const alias = aliasSlug(q, animals)
  if (alias) return { slug: alias, score: 1, via: 'alias' }

  const candidates = animals.map((a) => ({ slug: a.slug, text: normalize(a.name), words: normalize(a.name).split(' ') }))

  const exact = candidates.find((c) => c.text === q)
  if (exact) return { slug: exact.slug, score: 1, via: 'exact' }

  const qs = singular(q)
  let best = null
  for (const c of candidates) {
    const cs = singular(c.text)
    let score = similarity(qs, cs)
    // A spoken phrase that contains the whole name ("a big lion") should still hit.
    if (cs.includes(qs) || qs.includes(cs)) score = Math.max(score, 0.9)
    // Distinctive last word: "badger" -> "honey badger", but not "shark" (five of them).
    const lastWord = c.words[c.words.length - 1]
    if (qs === singular(lastWord) && candidates.filter((o) => o.words[o.words.length - 1] === lastWord).length === 1) {
      score = Math.max(score, 0.88)
    }
    // A misspelling usually targets one word, not the whole name: "badjer"
    // should reach "honey badger" (D-075). Word-level match needs to be
    // close, and only counts for words long enough to be distinctive.
    for (const word of c.words) {
      if (word.length < 5) continue
      const ws = similarity(qs, singular(word))
      if (ws >= 0.8) score = Math.max(score, ws * 0.95)
    }
    if (!best || score > best.score) best = { slug: c.slug, score, via: 'fuzzy' }
  }
  return best && best.score >= floor ? { ...best, score: Math.round(best.score * 100) / 100 } : null
}

/**
 * Parse a whole spoken matchup into two slugs.
 * @returns {{ok: boolean, a?, b?, matches?, heard: string, error?: string}}
 */
export function parseMatchup(text, animals) {
  const heard = String(text || '').trim()
  const parts = heard.split(SPLIT_RE).map((p) => p.trim()).filter(Boolean)
  if (parts.length < 2) return { ok: false, heard, error: 'need two animals — try "lion versus tiger"' }

  const [first, second] = [matchSlug(parts[0], animals), matchSlug(parts[1], animals)]
  if (!first) return { ok: false, heard, error: `couldn't find an animal called "${parts[0]}"`, unmatched: parts[0] }
  if (!second) return { ok: false, heard, error: `couldn't find an animal called "${parts[1]}"`, unmatched: parts[1] }
  if (first.slug === second.slug) return { ok: false, heard, error: 'heard the same animal twice' }

  return { ok: true, heard, a: first.slug, b: second.slug, matches: [first, second] }
}

/* ---------------- the search box: best match first (D-207) ---------------- */

/**
 * How well a card answers what was typed, best first (D-207). D-075's search
 * found the right cards and showed them in roster order, so "horse" put the
 * hippo — "the river horse" — ahead of the horse, and "dog" put fourteen
 * wolves and hyenas ahead of the dog.
 *
 * The name counts before the nickname, and the nickname before the tab the
 * card sits on; inside each, a whole word before one still being typed. The
 * last word of a name says what the animal is — a killer whale is a whale, a
 * whale shark is a shark — so it outranks the first; in a nickname that word
 * comes before "of" ("the tiger of the sky"). The one exception is a tab that
 * is one kind of animal: "bird" is the Birds tab before it is the goliath
 * birdeater, and "dinosaur" the Dinosaurs before the chicken, "the living
 * dinosaur". A word still being typed prefers the word it is closest to
 * finishing — "hor" is the horse before the horned owl — then the name's own
 * start; anything still tied keeps roster order.
 */
const TIER = {
  exact: 0, //       the whole name, or an alias for it: "pony" -> Horse, "orca" -> Killer Whale
  head: 1, //        the name's last word: "shark" -> Tiger Shark, "whale" -> Killer Whale
  lead: 2, //        its first word: "sea" -> Sea Otter, "tiger" -> Tiger Shark
  word: 3, //        a word in between: "sea" -> Leatherback Sea Turtle
  compound: 4, //    the end of its last word: "horse" -> Seahorse, "fish" -> Pufferfish
  tab: 5, //         the tab's whole name: "bird", "dinosaurs", "big cats"
  typing: 6, //      a word of the name still being typed: "tig" -> Tiger, "hor" -> Horse
  tabStart: 7, //    three letters of the tab's name or more: "dino" -> Dinosaurs
  nickHead: 8, //    the nickname's own word: "horse" -> Hippo, the river horse
  nickWord: 9, //    another word of it: "snake" -> Mongoose, the snake fighter
  tabWord: 10, //    one word of the tab's name: "dog" -> everything on Wolves & dogs
  inside: 11, //     inside a word of the name, four letters or more: "hors" -> Seahorse
  nickTyping: 12, // a nickname word still being typed: "cat" -> Caracal, the bird catcher
  tabTyping: 13, //  a word of the tab's name still being typed
  nickInside: 14, // inside a nickname word, four letters or more
  tabInside: 15, //  inside a word of the tab's name, four letters or more: "hero" -> Superheroes
}
/** Below this many letters a word being typed could be anything, and roster order is the better guess. */
const CLOSEST_FROM = 3
/** What each kind of match (wordMatch) is worth in a name, a nickname and a tab's name. */
const NAME_TIERS = { exact: TIER.exact, head: TIER.head, lead: TIER.lead, word: TIER.word, start: TIER.typing, prefix: TIER.typing }
const NICK_TIERS = { exact: TIER.nickHead, head: TIER.nickHead, lead: TIER.nickWord, word: TIER.nickWord, start: TIER.nickTyping, prefix: TIER.nickTyping }
const TAB_TIERS = { exact: TIER.tab, head: TIER.tabWord, lead: TIER.tabWord, word: TIER.tabWord, start: TIER.tabTyping, prefix: TIER.tabTyping }

/**
 * What a typed word is compared as: lowercase, accents off, anything else a
 * space. "André the Giant" -> "andre the giant", "Three-Horned" -> "three
 * horned". Not normalize(): that one is for speech and keys the alias table,
 * and it drops "me", "who" and "win", the start of "meerkat" and "wing".
 */
const fold = (text) =>
  String(text || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()

const words = (text) => fold(text).split(' ').filter(Boolean)
const ARTICLES = new Set(['the', 'a', 'an'])
const STOPWORDS = new Set([...ARTICLES, 'of', 'and'])
const contentWords = (list) => list.filter((w) => !STOPWORDS.has(w))

/** The forms a word may take: "wolves" -> wolf, "foxes" -> fox, "puppies" -> puppy. */
function forms(w) {
  const out = [w]
  if (w.length >= 4 && w.endsWith('s')) out.push(w.slice(0, -1))
  if (w.length >= 5 && w.endsWith('es')) out.push(w.slice(0, -2))
  if (w.length >= 5 && w.endsWith('ies')) out.push(`${w.slice(0, -3)}y`)
  if (w.length >= 5 && w.endsWith('ves')) out.push(`${w.slice(0, -3)}f`)
  return out
}
const sameWord = (a, b) => a === b || forms(a).some((x) => forms(b).includes(x))

/**
 * Where the query's words sit in a list of words: the index of the first one,
 * or -1. With `typing`, the query's last word may be the start of a word — the
 * kid is still typing it; without, every word has to be whole.
 */
function locate(list, query, typing) {
  const last = query.length - 1
  for (let i = 0; i + query.length <= list.length; i++) {
    if (query.every((q, j) => (typing && j === last ? list[i + j].startsWith(q) : sameWord(list[i + j], q)))) return i
  }
  return -1
}

/**
 * How the query sits in a list of words: { kind, left } or null. `kind` is
 * 'exact' | 'head' | 'lead' | 'word' for whole words and 'start' | 'prefix'
 * for a word still being typed, whose `left` is how many letters it has to
 * go. `head` is the index of the word that says what the thing is.
 */
function wordMatch(list, query, head) {
  const whole = locate(list, query, false)
  if (whole >= 0) {
    const end = whole + query.length - 1
    if (whole === 0 && end === list.length - 1) return { kind: 'exact', left: 0 }
    return { kind: end === head ? 'head' : whole === 0 ? 'lead' : 'word', left: 0 }
  }
  const at = locate(list, query, true)
  if (at < 0) return null
  const typing = query.at(-1)
  const left = typing.length >= CLOSEST_FROM ? list[at + query.length - 1].length - typing.length : 0
  return { kind: at === 0 ? 'start' : 'prefix', left }
}

/** Inside one word ("hors" -> Seahorse), or a name typed without its space ("tigershark"). */
function inside(list, typed, { spaceless = false } = {}) {
  if (typed.length < 4) return false
  if (list.some((w) => w.includes(typed))) return true
  // Only a name, and only from its start, into its second word: in a
  // nickname "seal" would be "the deep sea lantern".
  return spaceless && list.length > 1 && typed.length > list[0].length && list.join('').startsWith(typed)
}

/** One card's words, folded once per search. */
function cardWords(animal, label) {
  const name = words(animal.name)
  // "The Rock" is also just "rock": a leading "the" is part of the name, not the start of it.
  const names = name[0] === 'the' && name.length > 1 ? [name, name.slice(1)] : [name]
  const nickAll = words(animal.nickname)
  const nick = contentWords(nickAll)
  const of = nickAll.indexOf('of')
  const nickHead = nick.lastIndexOf(contentWords(of > 0 ? nickAll.slice(0, of) : nickAll).at(-1))
  return { name, names, nick, nickHead, tab: contentWords(words(label)) }
}

/**
 * The best this card does for the query, or null. The whole number is the
 * tier; the fraction orders a word still being typed by the letters it has
 * left, then puts the start of the name before a later word.
 */
function scoreOf(card, nameQuery, restQuery) {
  let best = null
  const take = (tier, { kind, left = 0 } = {}) => {
    const score = tier + Math.min(left, 99) / 100 + (kind === 'prefix' ? 0.005 : 0)
    if (best == null || score < best) best = score
  }
  const takeMatch = (m, tiers) => { if (m) take(tiers[m.kind], m) }

  for (const list of card.names) takeMatch(wordMatch(list, nameQuery, list.length - 1), NAME_TIERS)
  const typed = nameQuery.join('')
  // A compound's last part: "seahorse", "swordfish", "bobcat". Three letters
  // of it or more, and three before it, or "ant" would find every giant.
  if (nameQuery.length === 1 && typed.length >= 3) {
    const last = card.name.at(-1)
    if (forms(typed).some((f) => last !== f && last.length - f.length >= 3 && last.endsWith(f))) take(TIER.compound)
  }
  if (card.names.some((list) => inside(list, typed, { spaceless: true }))) take(TIER.inside)

  if (!restQuery.length) return best
  const rest = restQuery.join('')
  takeMatch(wordMatch(card.nick, restQuery, card.nickHead), NICK_TIERS)
  if (inside(card.nick, rest)) take(TIER.nickInside)
  // "Bears and large mammals" is not one kind of animal, so a bear word there
  // is only a word of the tab; "dino" is on its way to the whole name.
  const tab = wordMatch(card.tab, restQuery, -1)
  if (tab?.kind === 'start' && rest.length >= 3) take(TIER.tabStart)
  else takeMatch(tab, TAB_TIERS)
  if (inside(card.tab, rest)) take(TIER.tabInside)
  return best
}

/** Every slug an alias names for this query, the plural's too: "cats" -> House Cat. */
function aliasHits(query, animals) {
  const said = normalize(query).split(' ').filter(Boolean)
  if (!said.length) return new Set()
  const last = said.pop()
  return new Set(forms(last).map((f) => aliasSlug([...said, f].join(' '), animals)).filter(Boolean))
}

/**
 * Roster search for the grid: every card that matches, best match first
 * (D-075, D-207). Aliases, plurals, nicknames and the tab's name all match,
 * as they always have; `TIER` is the order they come back in. A near-miss
 * ("tigar", "chickn") goes to the fuzzy matcher only when nothing else
 * matched. Returns [] only when nothing is close.
 */
export function searchRoster(query, animals, { groups = [] } = {}) {
  const typed = words(query)
  if (!typed.length) return animals
  const nameQuery = typed.length > 1 && ARTICLES.has(typed[0]) ? typed.slice(1) : typed
  const restQuery = contentWords(typed)

  const labelFor = new Map(groups.flatMap((g) => g.slugs.map((s) => [s, g.label])))
  const alias = aliasHits(query, animals)
  const ranked = []
  animals.forEach((animal, i) => {
    const score = alias.has(animal.slug) ? TIER.exact : scoreOf(cardWords(animal, labelFor.get(animal.slug)), nameQuery, restQuery)
    if (score != null) ranked.push({ animal, score, i })
  })
  if (ranked.length) return ranked.sort((x, y) => x.score - y.score || x.i - y.i).map((r) => r.animal)

  const m = matchSlug(query, animals, { floor: 0.72 })
  return m ? animals.filter((a) => a.slug === m.slug) : []
}

export default { matchSlug, parseMatchup, normalize, searchRoster, singular }
